import './index.css'
import { ThemeProvider } from 'next-themes'
import { useEffect } from 'react'
import { CompaniesTable } from '@/components/CompaniesTable'
import { CompanyPage } from '@/components/CompanyPage'
import { Login } from '@/components/Login'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Toaster } from '@/components/ui/sonner'
import { ViewTransition } from '@/components/ViewTransition'
import { useBoundStore } from '@/store/useBoundStore'
import { Header } from './components/Header'

function AppContent() {
	const user = useBoundStore((state) => state.user)
	const loading = useBoundStore((state) => state.loading)
	const initialize = useBoundStore((state) => state.initialize)
	const isAuthenticated = useBoundStore((state) => state.isAuthenticated)
	const isAdmin = useBoundStore((state) => state.isAdmin)
	const isManager = useBoundStore((state) => state.isManager)
	const isEmployee = useBoundStore((state) => state.isEmployee)
	const company = useBoundStore((state) => state.company)
	const setCompany = useBoundStore((state) => state.setCompany)

	useEffect(() => {
		void initialize()
	}, [initialize])

	let viewKey = 'loading'
	let content = (
		<div className="container mx-auto p-8 text-center">
			<p>Cargando...</p>
		</div>
	)

	if (!loading) {
		if (!isAuthenticated()) {
			viewKey = 'login'
			content = (
				<div className="container mx-auto flex min-h-full items-center justify-center p-8">
					<Login />
				</div>
			)
		} else if (isAdmin() && company) {
			viewKey = `company-${company.id}`
			content = (
				<CompanyPage
					company={company}
					onBack={() => setCompany(null)}
					onCompanyUpdate={(updatedCompany) => {
						setCompany(updatedCompany)
					}}
				/>
			)
		} else if (isManager() && user?.company) {
			viewKey = `manager-company-${user.company.id}`
			content = (
				<CompanyPage
					company={user.company}
					onBack={() => {
						// Managers no pueden volver atrás, siempre están en su empresa
					}}
					onCompanyUpdate={(updatedCompany) => {
						const setUser = useBoundStore.getState().setUser
						if (user) {
							setUser({
								...user,
								company: updatedCompany,
							})
						}
					}}
				/>
			)
		} else if (isAdmin()) {
			viewKey = 'companies'
			content = <CompaniesTable onCompanyClick={setCompany} />
		} else {
			viewKey = 'dashboard'
			content = (
				<div className="container mx-auto p-8">
					<div className="max-w-4xl mx-auto space-y-6 pt-12">
						<div>
							<h1 className="text-3xl font-bold">Bienvenido, {user?.name}</h1>
							<p className="text-muted-foreground">Código: {user?.code}</p>
						</div>

						<Card>
							<CardHeader>
								<CardTitle>Información del usuario</CardTitle>
								<CardDescription>Detalles de tu cuenta</CardDescription>
							</CardHeader>
							<CardContent className="space-y-4">
								<div>
									<p className="text-sm text-muted-foreground">ID</p>
									<p className="font-medium">{user?.id}</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Código</p>
									<p className="font-medium">{user?.code}</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Nombre</p>
									<p className="font-medium">{user?.name}</p>
								</div>
								<div>
									<p className="text-sm text-muted-foreground">Tipo de usuario</p>
									<div className="mt-2 flex gap-2">
										{isAdmin() && (
											<span className="inline-flex items-center rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
												Administrador
											</span>
										)}
										{isManager() && (
											<span className="inline-flex items-center rounded-full bg-blue-500/10 px-3 py-1 text-xs font-medium text-blue-500">
												Manager
											</span>
										)}
										{isEmployee() && (
											<span className="inline-flex items-center rounded-full bg-green-500/10 px-3 py-1 text-xs font-medium text-green-500">
												Empleado
											</span>
										)}
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				</div>
			)
		}
	}

	return (
		<div className="flex min-h-screen flex-col">
			<Header />
			<main className="relative z-10 flex-1 py-4">
				<ViewTransition stateKey={viewKey}>{content}</ViewTransition>
			</main>
		</div>
	)
}

export function App() {
	return (
		<ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
			<AppContent />
			<Toaster />
		</ThemeProvider>
	)
}

export default App
